import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCms } from '../../contexts/CmsContext';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useKeyboard } from '../../hooks/useKeyboard';
import { getFeaturedCourse, getUpcomingWorkshops, getCourses, getBanners } from '../../api/client';
import FeaturedCourseLanding from './FeaturedCourseLanding';
import CodeRainBackground from './CodeRainBackground';
import TestimonialsSection from './TestimonialsSection';
import NewsletterSection from './NewsletterSection';
import StructuredData from './StructuredData';
import NewsTickerSection from './NewsTickerSection';
import SkipLink from '../../components/a11y/SkipLink';
import Currency, { CurrencyCompare } from '../../components/Currency';
import type { Course } from '../../types/course';
import {
  Calendar,
  Clock,
  ArrowRight,
  BookOpen,
  Zap,
  Users,
  Tag,
  Sparkles,
  Menu,
  X,
  ChevronUp,
  Percent,
  Bell,
  MapPin,
  Target,
  Shield,
  Award,
  Search,
  ArrowUpRight,
  Play,
  Newspaper,
  ExternalLink,
} from 'lucide-react';

const iconMap: Record<string, React.ElementType> = { Target, Users, Award, Shield, Zap, BookOpen };

// ─── FOCUS VISIBLE UTILITY ───
const focusClasses = 'focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2';

// ─── MAIN LANDING PAGE ───
export default function LandingPage(): React.JSX.Element {
  const { isAuthenticated } = useAuth();
  const cms = useCms();
  const navigate = useNavigate();
  const scrollPos = useScrollPosition(100);
  const reducedMotion = useReducedMotion();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredCourse, setFeaturedCourse] = useState<Course | null>(null);
  const [loadingFeatured, setLoadingFeatured] = useState(true);

  // Data states
  const [upcomingWorkshops, setUpcomingWorkshops] = useState<any[]>([]);
  const [upcomingCourses, setUpcomingCourses] = useState<Course[]>([]);
  const [promoCourses, setPromoCourses] = useState<Course[]>([]);
  const [banners, setBanners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Scroll-aware navbar: always light since hero is white
  const isOverDark = false;
  const showStickyCta = scrollPos.y > 500 && scrollPos.direction === 'down';

  // CMS content
  const highlightCards = cms.getJson('landing_highlights_cards_json', []);

  const defaultHighlights = [
    { icon: 'Target', label: 'Methodology', title: 'Research-First Approach', desc: 'Every curriculum is built on real industry research, not recycled content.', accent: false },
    { icon: 'Shield', label: 'Privacy', title: 'Local AI Models', desc: 'Train and deploy models on-premises. RBI-compliant, zero data leakage.', accent: true },
    { icon: 'Users', label: 'Community', title: 'Peer Review Network', desc: 'Learn alongside working professionals. Share findings, review projects.', accent: false },
    { icon: 'Zap', label: 'Projects', title: 'Portfolio-Grade Work', desc: 'Ship 6 BFSI projects you can put on your resume and defend in interviews.', accent: false },
    { icon: 'BookOpen', label: 'Mentorship', title: 'Office Hours', desc: 'Weekly live sessions with practitioners who have built AI in regulated environments.', accent: false },
    { icon: 'Award', label: 'Outcomes', title: 'Career Acceleration', desc: 'Structured pathways from analyst to AI lead, with measurable skill benchmarks.', accent: false },
  ];

  const highlights = highlightCards.length > 0
    ? highlightCards.map((h: any) => ({ ...h, icon: h.icon || 'Target' }))
    : defaultHighlights;

  // Fetch featured course
  useEffect(() => {
    const featuredId = cms.get('featured_course_id', '');
    if (!featuredId) {
      setLoadingFeatured(false);
      return;
    }
    getFeaturedCourse()
      .then((res) => {
        if (res.course) {
          setFeaturedCourse(res.course as Course);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingFeatured(false));
  }, [cms]);

  // Fetch home page data
  useEffect(() => {
    setLoading(true);
    Promise.all([
      getUpcomingWorkshops().catch(() => ({ workshops: [] })),
      getCourses({ status: 'published', per_page: '6' }).catch(() => ({ data: [] })),
      getBanners().catch(() => ({ data: { banners: [] } })),
    ])
      .then(([wsRes, coursesRes, bannersRes]) => {
        const workshops = (wsRes as any).workshops || [];
        setUpcomingWorkshops(workshops);

        const allCourses = Array.isArray(coursesRes.data)
          ? coursesRes.data
          : coursesRes.data?.data || [];
        setUpcomingCourses(allCourses);

        const promo = allCourses.filter(
          (c: Course) => c.discount_price > 0 && c.discount_price < c.price
        );
        setPromoCourses(promo);

        const bannerList = bannersRes.data?.banners || [];
        setBanners(bannerList);
      })
      .finally(() => setLoading(false));
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/courses?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Close mobile menu on outside click
  useClickOutside(mobileMenuRef, () => {
    if (mobileMenuOpen) setMobileMenuOpen(false);
  });

  // Close mobile menu on Escape
  useKeyboard('Escape', () => {
    if (mobileMenuOpen) setMobileMenuOpen(false);
    if (searchOpen) setSearchOpen(false);
  });

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  if (loadingFeatured && featuredCourse) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!loadingFeatured && featuredCourse) {
    return <FeaturedCourseLanding course={featuredCourse} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SkipLink />
      <StructuredData courses={upcomingCourses} siteName={cms.get('site_name', 'EcoleMinds')} />

      {/* ═══════════════════════════════════════════════════
          NAVBAR — Scroll-aware dark/light theming
          ═══════════════════════════════════════════════════ */}
      <header>
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
            isOverDark
              ? 'bg-slate-900/60 backdrop-blur-xl border-white/10'
              : 'bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-sm'
          }`}
        >
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2 group">
                <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className={`text-xl font-bold tracking-tight transition-colors ${isOverDark ? 'text-white' : 'text-slate-900'}`}>
                  {cms.get('site_name', 'EcoleMinds')}
                </span>
              </Link>

              {/* Desktop Nav */}
              <div className="hidden md:flex items-center gap-6">
                {[
                  { label: 'Highlights', action: () => scrollTo('highlights') },
                  { label: 'Courses', action: () => scrollTo('courses') },
                  { label: 'Workshops', action: () => scrollTo('workshops') },
                  { label: 'Promotions', action: () => scrollTo('promotions') },
                ].map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={item.action}
                    className={`text-sm font-medium transition-colors ${focusClasses} ${
                      isOverDark
                        ? 'text-slate-300 hover:text-amber-400 focus:ring-offset-slate-900'
                        : 'text-slate-600 hover:text-amber-500 focus:ring-offset-white'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                <Link
                  to="/blog"
                  className={`text-sm font-medium transition-colors ${focusClasses} ${
                    isOverDark
                      ? 'text-slate-300 hover:text-amber-400 focus:ring-offset-slate-900'
                      : 'text-slate-600 hover:text-amber-500 focus:ring-offset-white'
                  }`}
                >
                  Blog
                </Link>
              </div>

              <div className="flex items-center gap-3">
                {/* Search toggle */}
                <button
                  type="button"
                  onClick={() => setSearchOpen(!searchOpen)}
                  aria-label="Toggle search"
                  aria-expanded={searchOpen}
                  className={`hidden sm:flex p-2 rounded-xl transition-colors ${focusClasses} ${
                    isOverDark
                      ? 'text-slate-300 hover:bg-white/10 focus:ring-offset-slate-900'
                      : 'text-slate-600 hover:bg-slate-100 focus:ring-offset-white'
                  }`}
                >
                  <Search className="w-5 h-5" />
                </button>

                {isAuthenticated ? (
                  <Link to="/my-courses" className="hidden sm:block">
                    <button
                      type="button"
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors shadow-lg ${focusClasses} ${
                        isOverDark
                          ? 'bg-amber-500 text-white hover:bg-amber-400 shadow-amber-500/25 focus:ring-offset-slate-900'
                          : 'bg-amber-500 text-white hover:bg-amber-600 shadow-amber-500/20 focus:ring-offset-white'
                      }`}
                    >
                      My Courses
                    </button>
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className={`hidden sm:block text-sm font-medium transition-colors ${focusClasses} ${
                      isOverDark
                        ? 'text-slate-300 hover:text-amber-400 focus:ring-offset-slate-900'
                        : 'text-slate-600 hover:text-amber-500 focus:ring-offset-white'
                    }`}
                  >
                    Sign In
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Toggle navigation menu"
                  aria-expanded={mobileMenuOpen}
                  aria-controls="mobile-menu"
                  className={`md:hidden p-2 rounded-xl transition-colors ${focusClasses} ${
                    isOverDark
                      ? 'text-slate-300 hover:bg-white/10 focus:ring-offset-slate-900'
                      : 'text-slate-600 hover:bg-slate-100 focus:ring-offset-white'
                  }`}
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Search bar expand */}
            {searchOpen && (
              <motion.form
                initial={reducedMotion ? undefined : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={handleSearch}
                className="pb-3"
              >
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search courses, topics, skills..."
                    className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm border transition-all ${focusClasses} ${
                      isOverDark
                        ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500 focus:ring-offset-slate-900'
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-offset-white'
                    }`}
                  />
                </div>
              </motion.form>
            )}
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              id="mobile-menu"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile navigation menu"
              initial={reducedMotion ? undefined : { opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white/10 bg-slate-900/95 backdrop-blur-xl overflow-hidden"
            >
              <div className="px-4 py-3 space-y-1">
                {['Highlights', 'Courses', 'Workshops', 'Promotions', 'Blog'].map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => {
                      if (item === 'Highlights') scrollTo('highlights');
                      else if (item === 'Courses') scrollTo('courses');
                      else if (item === 'Workshops') scrollTo('workshops');
                      else if (item === 'Promotions') scrollTo('promotions');
                      else navigate(`/${item.toLowerCase()}`);
                      setMobileMenuOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${focusClasses} text-slate-300 hover:bg-white/10 focus:ring-offset-slate-900`}
                  >
                    {item}
                  </button>
                ))}
                <div className="border-t border-white/10 my-2" />
                {isAuthenticated ? (
                  <>
                    <Link to="/my-courses" onClick={() => setMobileMenuOpen(false)} className={`block px-4 py-3 rounded-xl text-sm font-semibold text-amber-400 hover:bg-amber-500/10 transition-colors ${focusClasses} focus:ring-offset-slate-900`}>My Courses</Link>
                    <Link to="/profile" onClick={() => setMobileMenuOpen(false)} className={`block px-4 py-3 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/10 transition-colors ${focusClasses} focus:ring-offset-slate-900`}>Profile</Link>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className={`block px-4 py-3 rounded-xl text-sm font-semibold text-amber-400 hover:bg-amber-500/10 transition-colors ${focusClasses} focus:ring-offset-slate-900`}>Sign In</Link>
                )}
              </div>
            </motion.div>
          )}
        </nav>
      </header>

      {/* ═══════════════════════════════════════════════════
          HERO — White creative with code rain background
          ═══════════════════════════════════════════════════ */}
      <section
        aria-label="Hero section"
        className="relative pt-32 pb-24 px-4 overflow-hidden bg-white"
      >
        <CodeRainBackground />

        {/* Ambient glow orbs */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-[120px] pointer-events-none" aria-hidden="true" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-sky-500/10 rounded-full blur-[100px] pointer-events-none" aria-hidden="true" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none" aria-hidden="true" />

        {/* Grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06]"
          aria-hidden="true"
          style={{
            backgroundImage: `linear-gradient(rgba(245,166,35,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(245,166,35,0.5) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.span
            initial={reducedMotion ? undefined : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/20 mb-6 backdrop-blur-sm"
          >
            {cms.get('landing_hero_label', '12-Week Live Cohort for BFSI Professionals')}
          </motion.span>

          <motion.h1
            initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reducedMotion ? 0 : 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight"
          >
            {cms.get('landing_hero_title', 'Master AI for Finance in 12 Weeks')}
          </motion.h1>

          <motion.div
            initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reducedMotion ? 0 : 0.2 }}
            className="mb-8"
          >
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {cms.get('landing_hero_tagline', 'Practical AI skills for CAs, Bankers, FP&A, Risk & Fintech professionals. No heavy coding. Includes Local Private Models for RBI-compliant work.')}
            </p>
            {/* Code-style decorative line — improved contrast */}
            <div className="mt-4 flex items-center justify-center gap-2 text-xs font-mono text-slate-500">
              <span className="text-amber-500/80">$</span>
              <span className="text-slate-500">python -m learn.ai --finance --local --compliant</span>
              {!reducedMotion && (
                <motion.span
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="w-2 h-4 bg-amber-500/80 inline-block"
                />
              )}
              {reducedMotion && <span className="w-2 h-4 bg-amber-500/80 inline-block" />}
            </div>
          </motion.div>

          <motion.div
            initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: reducedMotion ? 0 : 0.3 }}
            className="flex flex-wrap items-center justify-center gap-4"
          >
            <Link
              to="/courses"
              className={`group relative px-8 py-3.5 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-400 transition-all shadow-lg shadow-amber-500/30 flex items-center gap-2 overflow-hidden ${focusClasses} focus:ring-offset-white`}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" aria-hidden="true" />
              Browse Courses <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/workshops"
              className={`px-8 py-3.5 border-2 border-slate-300 text-slate-600 rounded-xl font-bold hover:border-amber-500/50 hover:text-amber-500 transition-all backdrop-blur-sm ${focusClasses} focus:ring-offset-white`}
            >
              View Workshops
            </Link>
          </motion.div>

          {/* Floating code badges */}
          <motion.div
            initial={reducedMotion ? undefined : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: reducedMotion ? 0 : 0.6 }}
            className="mt-12 flex flex-wrap items-center justify-center gap-3"
          >
            {['Python', 'TensorFlow', 'PyTorch', 'Scikit-Learn', 'Local LLMs'].map((tag, i) => (
              <motion.span
                key={tag}
                initial={reducedMotion ? undefined : { opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: reducedMotion ? 0 : 0.7 + i * 0.1 }}
                className="px-3 py-1 rounded-lg text-xs font-mono font-medium bg-slate-100 border border-slate-200 text-slate-600 backdrop-blur-sm"
              >
                {tag}
              </motion.span>
            ))}
          </motion.div>
        </div>

        {/* Bottom fade to next section */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" aria-hidden="true" />
      </section>

      <main id="main-content">
        {/* ═══════════════════════════════════════════════════
            STATS BAR
            ═══════════════════════════════════════════════════ */}
        <section aria-label="Statistics" className="py-12 px-4 bg-white border-b border-slate-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[
                { num: cms.get('home_stat_1_number', '2,000+'), label: cms.get('home_stat_1_label', 'Professionals Trained'), icon: Users },
                { num: cms.get('home_stat_2_number', '6'), label: cms.get('home_stat_2_label', 'Portfolio Projects'), icon: Target },
                { num: cms.get('home_stat_3_number', '100%'), label: cms.get('home_stat_3_label', 'Local AI Privacy'), icon: Shield },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="group relative text-center p-6 rounded-2xl border border-slate-100 bg-white hover:shadow-xl hover:shadow-amber-500/5 hover:border-amber-200 transition-all"
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
                  <div className="relative">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-amber-50 flex items-center justify-center group-hover:bg-amber-500 group-hover:shadow-lg group-hover:shadow-amber-500/20 transition-all">
                      <stat.icon className="w-5 h-5 text-amber-500 group-hover:text-white transition-colors" />
                    </div>
                    <p className="text-3xl sm:text-4xl font-bold text-slate-900 group-hover:text-amber-500 transition-colors">{stat.num}</p>
                    <p className="text-sm text-slate-500 mt-1 font-medium">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            HIGHLIGHTS
            ═══════════════════════════════════════════════════ */}
        <section id="highlights" aria-label="Why this cohort" className="py-20 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <motion.span
                initial={reducedMotion ? undefined : { opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4"
              >
                {cms.get('landing_highlights_label', 'Why This Cohort')}
              </motion.span>
              <motion.h2
                initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl font-bold text-slate-900"
              >
                {cms.get('landing_highlights_heading', 'What Makes This Different')}
              </motion.h2>
              <motion.p
                initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-slate-600 mt-3 max-w-2xl mx-auto"
              >
                {cms.get('landing_highlights_subheading', 'We built this platform to fill every gap we saw in the market.')}
              </motion.p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {highlights.map((item: any, i: number) => {
                const Icon = iconMap[item.icon] || Target;
                return (
                  <motion.div
                    key={i}
                    initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className={`group relative bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-xl hover:shadow-amber-500/5 hover:-translate-y-1 hover:border-amber-200 transition-all duration-300 overflow-hidden ${item.accent ? 'border-t-4 border-t-rose-500' : ''}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-sky-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" />
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4 group-hover:bg-amber-500 group-hover:shadow-lg group-hover:shadow-amber-500/25 transition-all duration-300">
                        <Icon className="w-6 h-6 text-amber-500 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${item.accent ? 'text-rose-500' : 'text-amber-500'}`}>{item.label}</p>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">{item.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            UPCOMING COURSES
            ═══════════════════════════════════════════════════ */}
        <section id="courses" aria-label="Upcoming courses" className="py-20 px-4 bg-white border-y border-slate-100">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
              <div>
                <motion.span
                  initial={reducedMotion ? undefined : { opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4"
                >
                  Catalogue
                </motion.span>
                <motion.h2
                  initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-3xl sm:text-4xl font-bold text-slate-900"
                >
                  Upcoming Courses
                </motion.h2>
                <motion.p
                  initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="text-slate-600 mt-2 max-w-xl"
                >
                  Recently published programs open for enrollment.
                </motion.p>
              </div>
              <Link
                to="/courses"
                className={`inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors ${focusClasses}`}
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-live="polite" aria-busy="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="aspect-video bg-slate-100 animate-pulse" />
                    <div className="p-5 space-y-3">
                      <div className="h-3 bg-slate-100 rounded w-1/3 animate-pulse" />
                      <div className="h-5 bg-slate-100 rounded w-3/4 animate-pulse" />
                      <div className="h-3 bg-slate-100 rounded w-full animate-pulse" />
                      <div className="h-3 bg-slate-100 rounded w-2/3 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : upcomingCourses.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingCourses.map((course, idx) => (
                  <motion.div
                    key={course.id}
                    initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Link
                      to={`/courses/${course.slug || course.id}`}
                      aria-label={`View ${course.title} course`}
                      className={`group block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-amber-500/5 hover:-translate-y-1 hover:border-amber-200 transition-all duration-300 h-full ${focusClasses}`}
                    >
                      {course.course_image ? (
                        <div className="aspect-video bg-slate-100 overflow-hidden relative">
                          <img src={course.course_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
                        </div>
                      ) : (
                        <div className="aspect-video bg-slate-100 flex items-center justify-center group-hover:bg-amber-50 transition-colors duration-300">
                          <BookOpen className="text-slate-300 group-hover:text-amber-300 transition-colors" size={32} />
                        </div>
                      )}
                      <div className="p-5 relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" aria-hidden="true" />
                        <div className="relative">
                          <div className="flex items-center gap-2 mb-2">
                            {course.category_name && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 group-hover:bg-amber-500 group-hover:text-white transition-colors">{course.category_name}</span>
                            )}
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 text-slate-600">{(course as any).level || 'All Levels'}</span>
                          </div>
                          <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-amber-600 transition-colors">{course.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-4">{course.short_description}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
                            <span className="flex items-center gap-1"><Clock size={12} /> {course.duration || 'Self-paced'}</span>
                          </div>
                          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                            <CurrencyCompare price={course.price} discountPrice={course.discount_price > 0 && course.discount_price < course.price ? course.discount_price : undefined} />
                            <span className="text-sm font-semibold text-amber-600 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                              View <ArrowRight size={14} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <BookOpen size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">No courses available</h3>
                <p className="text-slate-500 mb-6">Check back soon for new programs</p>
                <Link
                  to="/courses"
                  className={`inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-600 transition-colors ${focusClasses}`}
                >
                  Browse All Courses <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            UPCOMING WORKSHOPS
            ═══════════════════════════════════════════════════ */}
        <section id="workshops" aria-label="Upcoming workshops" className="py-20 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
              <div>
                <motion.span
                  initial={reducedMotion ? undefined : { opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4"
                >
                  Live Sessions
                </motion.span>
                <motion.h2
                  initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-3xl sm:text-4xl font-bold text-slate-900"
                >
                  Upcoming Workshops
                </motion.h2>
                <motion.p
                  initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="text-slate-600 mt-2 max-w-xl"
                >
                  Hands-on sessions with industry practitioners. Limited seats available.
                </motion.p>
              </div>
              <Link
                to="/workshops"
                className={`inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors ${focusClasses}`}
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-live="polite" aria-busy="true">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3">
                    <div className="h-3 bg-slate-100 rounded w-1/4 animate-pulse" />
                    <div className="h-5 bg-slate-100 rounded w-3/4 animate-pulse" />
                    <div className="h-3 bg-slate-100 rounded w-full animate-pulse" />
                    <div className="h-3 bg-slate-100 rounded w-1/2 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : upcomingWorkshops.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingWorkshops.map((w, idx) => (
                  <motion.div
                    key={w.id}
                    initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <div className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-amber-500/5 hover:-translate-y-1 hover:border-amber-200 transition-all duration-300 h-full flex flex-col relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" aria-hidden="true" />
                      <div className="p-6 flex-1 relative">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                            {w.status === 'upcoming' ? 'Upcoming' : 'Open'}
                          </span>
                          <span className="text-xs text-slate-400">{w.duration || '2 hours'}</span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-amber-600 transition-colors">{w.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-4">{w.description || w.short_description || 'Join this hands-on workshop led by industry experts.'}</p>
                        <div className="space-y-2 mb-5">
                          {w.start_date && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Calendar size={14} className="text-slate-400" />
                              <span>{new Date(w.start_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                          )}
                          {w.start_time && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Clock size={14} className="text-slate-400" />
                              <span>{w.start_time}</span>
                            </div>
                          )}
                          {w.meeting_link && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <MapPin size={14} className="text-slate-400" />
                              <span>Online via Zoom</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="px-6 pb-6 pt-0 relative">
                        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                          <div>
                            <Currency value={w.price || 0} className="text-lg font-bold text-slate-900" showFree />
                            {w.max_seats && (
                              <span className="text-xs text-slate-400 ml-2">{w.seats_filled || 0}/{w.max_seats} seats</span>
                            )}
                          </div>
                          <Link
                            to={`/workshops/${w.id}`}
                            aria-label={`View details for ${w.title} workshop`}
                            className={`px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-amber-500 hover:shadow-lg hover:shadow-amber-500/25 transition-all flex items-center gap-1 group/btn ${focusClasses}`}
                          >
                            Details <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">No upcoming workshops</h3>
                <p className="text-slate-500 mb-6">Check back soon for new live sessions</p>
                <Link
                  to="/workshops"
                  className={`inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-600 transition-colors ${focusClasses}`}
                >
                  Browse All Workshops <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════
            PROMOTIONS
            ═══════════════════════════════════════════════════ */}
        <section id="promotions" aria-label="Promotions" className="py-20 px-4 bg-white border-y border-slate-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <motion.span
                initial={reducedMotion ? undefined : { opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-4"
              >
                Special Offers
              </motion.span>
              <motion.h2
                initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl font-bold text-slate-900"
              >
                Promotional Courses & Workshops
              </motion.h2>
              <motion.p
                initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-slate-600 mt-3 max-w-2xl mx-auto"
              >
                Limited-time pricing on selected programs.
              </motion.p>
            </div>

            {(promoCourses.length > 0 || banners.length > 0) ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {banners.map((banner: any, idx: number) => (
                  <motion.a
                    key={`banner-${banner.id}`}
                    href={banner.button_url || '#'}
                    initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    className={`group block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-amber-500/5 hover:-translate-y-1 hover:border-amber-200 transition-all duration-300 ${focusClasses}`}
                  >
                    {banner.image && (
                      <div className="aspect-[21/9] bg-slate-100 overflow-hidden">
                        <img src={banner.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      </div>
                    )}
                    <div className="p-5 relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" aria-hidden="true" />
                      <div className="relative">
                        <div className="flex items-center gap-2 mb-2">
                          <Bell size={12} className="text-amber-500" />
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Announcement</span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-amber-600 transition-colors">{banner.title}</h3>
                        {banner.subtitle && <p className="text-sm text-slate-600">{banner.subtitle}</p>}
                      </div>
                    </div>
                  </motion.a>
                ))}
                {promoCourses.map((course, idx) => (
                  <motion.div
                    key={`promo-${course.id}`}
                    initial={reducedMotion ? undefined : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Link
                      to={`/courses/${course.slug || course.id}`}
                      aria-label={`View ${course.title} promotional course`}
                      className={`group block bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:shadow-amber-500/5 hover:-translate-y-1 hover:border-amber-200 transition-all duration-300 h-full ${focusClasses}`}
                    >
                      {course.course_image ? (
                        <div className="aspect-video bg-slate-100 overflow-hidden relative">
                          <img src={course.course_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
                        </div>
                      ) : (
                        <div className="aspect-video bg-slate-100 flex items-center justify-center group-hover:bg-amber-50 transition-colors duration-300">
                          <BookOpen className="text-slate-300 group-hover:text-amber-300 transition-colors" size={32} />
                        </div>
                      )}
                      <div className="p-5 relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" aria-hidden="true" />
                        <div className="relative">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 group-hover:bg-amber-500 group-hover:text-white transition-colors">{course.category_name || 'Course'}</span>
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 flex items-center gap-1 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                              <Percent size={10} /> Promo
                            </span>
                          </div>
                          <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-amber-600 transition-colors">{course.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2 mb-4">{course.short_description}</p>
                          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                            <div className="flex items-baseline gap-2">
                              <Currency value={course.discount_price} className="text-lg font-bold text-slate-900" />
                              <Currency value={course.price} className="text-sm text-slate-400 line-through" />
                            </div>
                            <span className="text-sm font-semibold text-amber-600 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                              View <ArrowRight size={14} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Tag size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">No active promotions</h3>
                <p className="text-slate-500 mb-6">Check back soon for special offers</p>
                <Link
                  to="/courses"
                  className={`inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-600 transition-colors ${focusClasses}`}
                >
                  Explore Courses <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </section>

        <NewsTickerSection />
        <TestimonialsSection />
        <NewsletterSection />
      </main>

      {/* ═══════════════════════════════════════════════════
          STICKY BOTTOM CTA
          ═══════════════════════════════════════════════════ */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${
          showStickyCta ? 'translate-y-0' : 'translate-y-full'
        }`}
        aria-hidden={!showStickyCta}
      >
        <div className="bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-lg">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="hidden sm:block">
              <p className="text-sm font-bold text-slate-900">Ready to master AI for Finance?</p>
              <p className="text-xs text-slate-500">Join 2,000+ professionals already learning.</p>
            </div>
            <div className="flex items-center gap-3 flex-1 sm:flex-none">
              <Link
                to="/courses"
                className={`flex-1 sm:flex-none px-5 py-2.5 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 ${focusClasses}`}
              >
                Browse Courses <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/workshops"
                className={`hidden sm:flex px-5 py-2.5 border-2 border-slate-200 text-slate-700 rounded-xl font-bold text-sm hover:border-amber-500 hover:text-amber-600 transition-colors ${focusClasses}`}
              >
                Workshops
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════════ */}
      <footer className="bg-white border-t border-slate-100 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900">{cms.get('site_name', 'EcoleMinds')}</span>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 max-w-sm">
                {cms.get('footer_tagline', 'Research-led education for finance professionals building the future with AI.')}
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: Zap, label: 'Live Cohorts' },
                  { icon: BookOpen, label: 'Self-Paced' },
                  { icon: Users, label: 'Community' },
                ].map((badge, i) => (
                  <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-100">
                    <badge.icon className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-xs font-medium text-slate-600">{badge.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-4">Explore</h4>
              <div className="space-y-2">
                <Link to="/courses" className={`block text-sm text-slate-600 hover:text-amber-500 transition-colors ${focusClasses}`}>Courses</Link>
                <Link to="/workshops" className={`block text-sm text-slate-600 hover:text-amber-500 transition-colors ${focusClasses}`}>Workshops</Link>
                <Link to="/blog" className={`block text-sm text-slate-600 hover:text-amber-500 transition-colors ${focusClasses}`}>Blog</Link>
                <Link to="/about" className={`block text-sm text-slate-600 hover:text-amber-500 transition-colors ${focusClasses}`}>About</Link>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-bold text-slate-900 mb-4">Legal</h4>
              <div className="space-y-2">
                <Link to="/terms" className={`block text-sm text-slate-600 hover:text-amber-500 transition-colors ${focusClasses}`}>Terms</Link>
                <Link to="/privacy" className={`block text-sm text-slate-600 hover:text-amber-500 transition-colors ${focusClasses}`}>Privacy</Link>
                <Link to="/refund-policy" className={`block text-sm text-slate-600 hover:text-amber-500 transition-colors ${focusClasses}`}>Refunds</Link>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">{cms.get('footer_copyright', 'Built for finance professionals who want to lead the AI transformation.')}</p>
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: reducedMotion ? 'auto' : 'smooth' })}
              className={`p-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors ${focusClasses}`}
              aria-label="Back to top"
            >
              <ChevronUp className="w-5 h-5" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
